import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Card, Skeleton, Space, Tag, Typography } from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import workshopService from '@/services/workshop.service';
import { holdLabel, qcLabel, STAGE_TAG_COLOR, stageLabel } from '@/pages/assets/workshop/workshopLabels';

const { Text } = Typography;

type Props = {
  pmvProfileId: string;
};

/** Equipment Passport Workshop strip — extends Passport; does not create a second passport. */
const WorkshopPassportStrip: React.FC<Props> = ({ pmvProfileId }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['workshop', 'passport-strip', pmvProfileId],
    queryFn: () => workshopService.passportStrip(pmvProfileId),
    enabled: Boolean(pmvProfileId),
    retry: false,
  });

  if (isLoading) {
    return (
      <Card size="small" title={<Space><ToolOutlined /> Workshop</Space>} style={{ marginBottom: 12 }}>
        <Skeleton active paragraph={{ rows: 2 }} />
      </Card>
    );
  }

  if (isError) {
    return null;
  }

  const raw = (data as any)?.workshop ?? data;
  const caseId = raw?.case_id || raw?.case?.id;
  const caseNumber = raw?.case_number || raw?.case?.case_number;
  const title = raw?.title || raw?.case?.title;
  const stage = raw?.execution_stage || raw?.execution?.execution_stage;
  const hold = raw?.hold_reason || raw?.execution?.hold_reason;
  const qc = raw?.qc_state || raw?.execution?.qc_state;

  if (!caseId && !stage) {
    return (
      <Card size="small" title={<Space><ToolOutlined /> Workshop</Space>} style={{ marginBottom: 12 }}>
        <Text type="secondary">No active workshop job for this equipment.</Text>
      </Card>
    );
  }

  return (
    <Card size="small" title={<Space><ToolOutlined /> Workshop</Space>} style={{ marginBottom: 12 }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        {caseNumber && (
          <Text>
            Case{' '}
            <Link to={`/asset/workshop/jobs/${caseId}`}>
              <strong>{caseNumber}</strong>
            </Link>
            {title ? ` — ${title}` : ''}
          </Text>
        )}
        <Space wrap>
          {stage && <Tag color={STAGE_TAG_COLOR[stage]}>{stageLabel(stage)}</Tag>}
          {hold && <Tag color="orange">{holdLabel(hold)}</Tag>}
          {qc && qc !== 'NONE' && <Tag>{qcLabel(qc)}</Tag>}
        </Space>
        {stage === 'READY_FOR_RELEASE' && (
          <Alert type="success" showIcon message="Ready for release — authorize from Workshop job workspace." />
        )}
        {caseId && <Link to={`/asset/workshop/jobs/${caseId}`}>Open workshop job →</Link>}
      </Space>
    </Card>
  );
};

export default WorkshopPassportStrip;
